import type { KpiCardSpec } from '~containers/analytics/kpis/card-spec';

/**
 * RPP dashboard seed cards, grouped by the design's sections.
 *
 * Several sections are wired to real envelope sources: `active-users`,
 * `new-users`, and `engagement` back overall-performance and technical-health
 * cards, `six12-purchases` backs the Business impact cards, `coupon-issued` and
 * `coupon-redeemed-rpp` back the Coupons section, `game-engagement-rpp` backs the Game
 * engagement section (its four number/rate counts plus the drop-off `columns`
 * funnel), and `engagement-drivers-rpp` backs the Badges and Bonus actions `bars`
 * cards.
 *
 * **Every card on this page reads real data.** No card is on the `placeholder`
 * source any more, and none should go back on it: placeholder invents values
 * and marks them "Sample", which is a design mock, not a dashboard. A metric
 * with no backing signal is left off the page until it has one. The two cards
 * that were dropped for exactly that reason are Games played by environment
 * (no platform signal on a game session) and, separately, the whole Acquisition
 * section (app-downloads donuts + headline, email/SMS opt-ins), whose endpoints
 * and KPI sources are untouched: `app-downloads` still backs the CT/CK Overview
 * download card.
 *
 * `description` feeds the (i) info popover, which is where every note about a
 * card lives: cards carry no footnotes.
 */

/** Section 1 — Overall performance: RPP-only single-value cards (Full app
 * dropped). Each reads the RPP cohort metric (`<metric>Rpp`) as a number/rate. */
export const OVERALL_PERFORMANCE_CARDS: KpiCardSpec[] = [
    {
        // Kept as "RPP registered users" by product: it is the name the business
        // knows this card by. It names a registration population and the number
        // is the visiting one, so the description below carries the definition.
        title: 'RPP registered users',
        source: 'login-activity-rpp',
        form: 'number',
        metric: 'activeUsersRpp',
        size: 'sm',
        description:
            'Registered players who actually opened RPP during the selected period, counted once each however many times they came back. Not the size of the register: a player who registered earlier and visited counts, one who registered without visiting does not. Spans both surfaces, the browser and the WebView opened from inside the app.',
    },
    {
        title: 'Weekly active users',
        source: 'login-activity-rpp',
        form: 'number',
        metric: 'weeklyActiveUsersRpp',
        size: 'sm',
        description:
            'Unique players who signed in during the 7 days ending on the selected period, with a period-over-period delta.',
    },
    {
        title: 'Daily active users',
        source: 'login-activity-rpp',
        form: 'number',
        metric: 'dailyActiveUsersRpp',
        size: 'sm',
        description:
            'Unique players who signed in on the last day of the selected period, with a period-over-period delta.',
    },
    {
        title: 'RPP unique players',
        source: 'new-users-rpp',
        form: 'number',
        metric: 'newUsersRpp',
        size: 'sm',
        description: 'First-time users in the selected period.',
    },
    {
        title: 'Repeat visit rate',
        source: 'engagement-rpp',
        form: 'rate',
        metric: 'repeatVisitRateRpp',
        size: 'sm',
        description:
            'Percentage of active players seen on two or more distinct days in the selected period. Counted from RPP sign-in activity, so a day counts once however many times the player came back that day.',
    },
    {
        title: 'Sessions per user',
        source: 'login-activity-rpp',
        form: 'number',
        metric: 'sessionsPerUserRpp',
        size: 'sm',
        description:
            'Average browser sign-ins per player who signed in at least once in the selected period. Browser only: a WebView session is opened by the native app and carries no sign-in.',
    },
];

/**
 * Section 2 — Business impact: five RPP-only single values, all from
 * `six12-purchases` (Full app dropped).
 *
 * The "Coupons issued vs redeemed" split card that used to sit here is gone.
 * It read the retired `coupons/issued-redeemed` endpoint, whose issued figure
 * is a different metric from the Coupons section's below (it joined no RPP
 * registration), so
 * keeping it would have put two disagreeing "coupons issued" numbers on one
 * page. Coupons now live in their own section, one concept per card.
 */
export const BUSINESS_IMPACT_CARDS: KpiCardSpec[] = [
    {
        title: 'In-store traffic / individuals',
        source: 'six12-purchases',
        metric: 'individuals',
        form: 'number',
        size: 'sm',
        description:
            'Unique app users who redeemed coupons in a physical store during the selected period.',
    },
    {
        title: 'Revenue via LIFT',
        source: 'six12-purchases',
        metric: 'revenue',
        form: 'number',
        size: 'sm',
        description: 'Attributable revenue measured through the LIFT platform',
    },
    {
        title: 'No. of lift transactions',
        source: 'six12-purchases',
        metric: 'transactions',
        form: 'number',
        size: 'sm',
        description:
            'Distinct in-store transactions attributed to app users. (tracked based on coupon redemption)',
    },
    {
        title: 'Avg. spent by transaction',
        source: 'six12-purchases',
        metric: 'avgSpend',
        form: 'number',
        size: 'sm',
        description: 'Average dollar amount spent per attributed transaction.',
    },
    {
        title: 'Items in basket',
        source: 'six12-purchases',
        metric: 'itemsPerBasket',
        form: 'number',
        size: 'sm',
        description:
            'Average number of distinct items per attributed transaction.',
    },
];

/** Section 3a — Game engagement: four hero counts across the top. */
export const GAME_ENGAGEMENT_CARDS: KpiCardSpec[] = [
    {
        title: 'Total opened game sessions',
        source: 'game-engagement-rpp',
        form: 'number',
        metric: 'gameSessionsOpened',
        size: 'sm',
        description:
            'Sessions where a user viewed the RPP game interface, whether or not they played.',
    },
    {
        title: 'Total games played',
        source: 'game-engagement-rpp',
        form: 'number',
        metric: 'gamesPlayed',
        size: 'sm',
        description: 'Number of completed game matches.',
    },
    {
        title: 'Average plays per user',
        source: 'game-engagement-rpp',
        form: 'number',
        metric: 'avgPlaysPerUser',
        size: 'sm',
        description: 'Total games played divided by unique RPP users.',
    },
    {
        title: 'Completion rate',
        source: 'game-engagement-rpp',
        form: 'rate',
        metric: 'gameCompletionRate',
        size: 'sm',
        description: 'Percentage of started game matches that reached the end.',
    },
];

/**
 * Section 3b — Game engagement charts: the drop-off funnel, alone.
 *
 * "Games played by environment" used to sit beside it on the `placeholder`
 * source, showing invented values under a "Sample" marker. There is no platform
 * signal on a game session to back it, so it was a design mock shipped in a
 * live dashboard, and it is gone. The row renders in a two-column grid, so the
 * `md` card that remains spans it fully rather than leaving a hole.
 */
export const GAME_ENGAGEMENT_CHART_CARDS: KpiCardSpec[] = [
    {
        title: 'Drop-off rate',
        source: 'game-engagement-rpp',
        form: 'columns',
        metric: 'gameDropOffRate',
        breakdownBy: 'round',
        size: 'md',
        description:
            'Per-round drop-off: of the players who reached each round, the share who then abandoned it (quit, or left an in-progress match from a past contest day). Finishing a match without a reward is not counted as a drop. The headline above the bars is the overall abandonment rate across all started matches. Hover a bar for the round breakdown.',
    },
];

/**
 * Section 4a — Coupons, the two headline counts. RPP-only, like every other
 * section on this page, which is what rules out the rest of the coupon surface
 * the API exposes:
 *
 * - `coupon-issued-rpp`'s `couponsIssuedRpp` and `coupon-redeemed-rpp`'s
 *   `couponsRedeemedRpp` are a real funnel: same half-open window, same
 *   RPP-registration province source, both covering every RPP promotion. That
 *   is why they can sit side by side and why their region rows line up.
 * - `couponsIssued` (whole app) is a different KPI on a different endpoint
 *   (`coupon-issued`), counting every promotion off the app-account province.
 *   It is deliberately NOT carded here, and it can no longer be reached by a
 *   one-keystroke slip on this page's source: the two live behind two sources.
 * - `coupon-activation`'s `claimed` is unusable on this page in either form:
 *   its query hardcodes `p.is_for_rpp = false`, so it is strictly the non-RPP
 *   half. It is not "all coupons", it is the complement of this page.
 *
 * There is still no redemption-rate card. The obstacle is semantic now, not
 * only structural: issued and redeemed no longer cover the same population
 * (issued keeps coupons released unredeemed at expiry, redeemed does not), and
 * a `rate` card reads one envelope while these are two endpoints, so it would
 * need a metric the API does not emit rather than a card the CMS does not have.
 */
export const COUPON_CARDS: KpiCardSpec[] = [
    {
        title: 'Coupons issued',
        source: 'coupon-issued-rpp',
        form: 'number',
        metric: 'couponsIssuedRpp',
        size: 'sm',
        description:
            'RPP coupons granted during the selected period, attributed to the region the player registered in. Counts grant rows, not people: one player holding several coupons counts several times. A coupon transferred to another player counts for its current holder. A coupon released unredeemed at expiry stays counted as issued.',
    },
    {
        title: 'Coupons redeemed',
        source: 'coupon-redeemed-rpp',
        form: 'number',
        metric: 'couponsRedeemedRpp',
        size: 'sm',
        description:
            'RPP coupons redeemed in store during the selected period, over the same window and region attribution as Coupons issued. Coupons issued also counts coupons released unredeemed at expiry; this count does not. Counts active redemptions only: a coupon reissued and then reclaimed counts once. A transferred coupon counts for its new holder.',
    },
];

/**
 * Section 4b — the same two metrics split across the four business units, as
 * donuts. Kept as its own row so each breakdown sits directly under its
 * headline count and the columns can be read down as well as across. The
 * slices sum to the headline on both cards.
 *
 * Both carry a permanent `Unknown 0` legend entry, and that is not a bug to
 * chase in the card: `UNKNOWN` is structurally always zero for these two
 * metrics, because the RPP registration join drops a grant or a redemption
 * without one rather than bucketing it. The pie form renders every slice it is
 * given, zeros included, which is the right default (a silently dropped zero
 * would hide a real "no residual" signal on other KPIs).
 */
export const COUPON_BREAKDOWN_CARDS: KpiCardSpec[] = [
    {
        title: 'Issued by business unit',
        source: 'coupon-issued-rpp',
        form: 'pie',
        metric: 'couponsIssuedRpp',
        breakdownBy: 'businessUnit',
        size: 'sm',
        description:
            'The same RPP coupons issued, split across the four business units. The rows sum to the headline count. Unknown is structurally always zero here: a grant whose holder has no RPP registration is dropped from the metric rather than bucketed.',
    },
    {
        title: 'Redeemed by business unit',
        source: 'coupon-redeemed-rpp',
        form: 'pie',
        metric: 'couponsRedeemedRpp',
        breakdownBy: 'businessUnit',
        size: 'sm',
        description:
            'The same RPP coupons redeemed, split across the four business units, on the same axis as Issued by business unit so the two can be compared region by region. The rows sum to the headline count.',
    },
];

/**
 * Section 5 — Technical health: three single cards. Error rate + Failed sessions
 * come from Sentry Release Health (source `technical-health`) and are national
 * only (Sentry sessions carry no province tag), flagged in their description.
 * Avg session length stays on the GA4-backed `engagement` source.
 */
export const TECHNICAL_HEALTH_CARDS: KpiCardSpec[] = [
    {
        title: 'Error rate',
        source: 'technical-health',
        form: 'rate',
        metric: 'errorRate',
        size: 'sm',
        description:
            'Share of sessions that hit an error, whether a handled error or a crash, from Sentry. Crashes also count toward Failed sessions. National only, not broken down by province.',
    },
    {
        title: 'Avg session length',
        source: 'engagement',
        form: 'number',
        metric: 'avgSessionLengthSeconds',
        size: 'sm',
        description:
            'Average engaged time per session, as GA4 measures it: foreground time only, not wall-clock from open to close. It will not match the GA4 interface exactly.',
    },
    {
        title: 'Failed sessions',
        source: 'technical-health',
        form: 'number',
        metric: 'failedSessions',
        size: 'sm',
        description:
            'Sessions that crashed or ended abnormally (the app was killed, hit an ANR, or was force-quit before the session closed cleanly), from Sentry. Crashed sessions also count toward Error rate. National only, not broken down by province.',
    },
];

/** Section 6, Engagement drivers: Badges + Bonus actions, each a `bars` list
 * over the RPP badge / bonus-action ledgers. */
export const ENGAGEMENT_DRIVER_CARDS: KpiCardSpec[] = [
    {
        title: 'Badges',
        source: 'engagement-drivers-rpp',
        form: 'bars',
        metric: 'badgeEarnRate',
        breakdownBy: 'badge',
        size: 'sm',
        description:
            'Percentage of RPP users who earned each badge during the selected period. One bar per available badge.',
        barBaseInHeader: true,
    },
    {
        title: 'Bonus actions',
        source: 'engagement-drivers-rpp',
        form: 'bars',
        metric: 'bonusCompletionRate',
        breakdownBy: 'bonusAction',
        size: 'sm',
        description:
            'Percentage of RPP users who completed each bonus action during the selected period. One bar per available action.',
        barBaseInHeader: true,
    },
];
